import { ChangeDetectorRef, ChangeDetectionStrategy, Component, OnInit, inject, signal, computed } from '@angular/core';
import { first } from 'rxjs';
import { APIService } from 'src/app/services/APIService/api.service';
import { ToastService } from 'src/app/services/toast.service';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ClarityModule } from '@clr/angular';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { PlotlyPlotComponent } from '../_components/plotly-plot/plotly-plot.component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { ColumnFilterAction } from '../_components/filter/column-filter/column-filter.component';
import { FiltersMap } from '../_components/filter/filter.service';
import { Row } from 'src/app/models/ui/table';
import { FilterItem } from 'src/app/models/ui/filter';
import { emptyValueSign } from 'src/app/models/ui/datatype';
import { PlotlyHTMLElement } from 'plotly.js-basic-dist-min';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [CommonModule, ClarityModule, FormsModule, ReactiveFormsModule, PlotlyPlotComponent, MrTranslatePipe],
})
export class DashboardComponent implements OnInit {
  private apiService = inject(APIService);
  private router = inject(Router);
  private toastr = inject(ToastService);
  private mrTranslate = inject(MrTranslatePipe);
  private sanitizer = inject(DomSanitizer);
  private basicStore = inject(BasicStoreService);
  protected localeDate = inject(BackendLocaleDatePipe);
  private changeDet = inject(ChangeDetectorRef);
  private overlayService = inject(OverlayService);

  graph: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: true,
      margin: {
        l: 10,
        r: 10,
        b: 10,
        t: 15,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphfk: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      barmode: 'group',
      bargroupgap: 0.15,
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphkav0: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: { range: [0, 1] },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphkav1: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 40,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        range: [0, 1],
      },
      xaxis: {
        title: 'X Values',
        type: 'category', // Important to preserve strings
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphkav2: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      //yaxis: { range: [0, 1] }
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphkav3: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      //yaxis: { range: [0, 1] }
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphkostenweek: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        rangemode: 'nonnegative',
        autorange: true,
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphkostenmonth: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        rangemode: 'nonnegative',
        autorange: true,
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphkostenyear: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        rangemode: 'nonnegative',
        autorange: true,
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphusernweek: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        rangemode: 'nonnegative',
        autorange: true,
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphusermonth: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        rangemode: 'nonnegative',
        autorange: true,
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  graphuseryear: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        rangemode: 'nonnegative',
        autorange: true,
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    },
    data: [],
  };

  optGroup: UntypedFormGroup = new UntypedFormGroup({
    show_BG: new UntypedFormControl(true),
    show_Sperr: new UntypedFormControl(true),
    show_Stoer: new UntypedFormControl(true),
    show_LF: new UntypedFormControl(true),
    show_Sonder: new UntypedFormControl(true),
    show_Anz_Insp: new UntypedFormControl(true),
    show_Anz_Wart: new UntypedFormControl(true),
    show_Anz_Auf: new UntypedFormControl(true),
    show_Anz_Begeh: new UntypedFormControl(true),
    show_Anz_EBL: new UntypedFormControl(true),
    show_Anz_Sonder: new UntypedFormControl(true),
    show_Anz_ALV: new UntypedFormControl(true),
    show_Dia_FK: new UntypedFormControl(true),
    show_Dia_KAV: new UntypedFormControl(true),
    show_Dia_Kost: new UntypedFormControl(true),
    show_Dia_Prog: new UntypedFormControl(true),
    show_Dia_Inst: new UntypedFormControl(true),
  });

  fkstatistic: any = [];

  protected readonly kavTitle = signal([
    { key: 5, value: 'Anlagenklasse' },
    { key: 1, value: 'Bezirk' },
    { key: 4, value: 'Erneuerungsbedarf Gleis' },
    { key: 3, value: 'Erneuerungsbedarf Weichen' },
    { key: 7, value: 'Eigentümer' },
    { key: 0, value: 'KAV Entwicklung' },
    { key: 2, value: 'Objekttyp' },
    { key: 8, value: 'Ort' },
    { key: 6, value: 'Stellwerk' },
    { key: 9, value: 'Verantwortlich' },
    { key: 10, value: 'Qualitätsindex' },
  ]);
  protected readonly diagramm = signal<Record<number, any[]>>({
    0: [],
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
    8: [],
    9: [],
    10: [],
  });

  kavTitel: string = 'KAV Entwicklung';
  anzBG: any;
  anzSperr: any;
  anzStoer: any;
  anzLF: any;
  anzSonder: any;
  anzInsp: any;
  InspStammIDs: FilterItem[] = [];
  anzWart: any;
  WartStammIDs: any;
  anzAuf: any;
  anzBegeh: any;
  protected readonly kavansicht = signal<any>(0);
  protected readonly curDiagramm = computed(() => this.diagramm()[this.kavansicht()]);
  curgraphkav: any;
  gesKAV: any;
  kostenweek: any = [];
  kostenmonth: any = [];
  kostenyear: any = [];
  kostenansicht: number = 0;
  kostenTitel: string = 'Kosten aktuelle Woche';
  userweek: any = [];
  usermonth: any = [];
  useryear: any = [];
  useransicht: number = 0;
  userTitel: string = 'Programmnutzung aktuelle Woche';
  opened: boolean = false;
  ih_average: any = [];
  ih_qoute: any = [];

  optChecked_EBL: number = 0;
  anzALV: number = 0;
  anzEBL: number = 0;
  ALVStammIDs: any = [];
  EBLStammIDs: any = [];
  anzSonderTermin: number;
  SondertOMAZSID: any = [];

  dtWRewNewdates: any;
  dtGRewNewdates: any;

  dia_ih_average: any;
  dia_ih_qoute: any;
  hasBegeh: boolean = false;
  table: any;

  changeKAVStatus() {
    if (this.kavansicht() == 10) {
      this.kavansicht.set(0);
    } else {
      this.kavansicht.update(value => value + 1);
    }

    const value = this.kavTitle().find(item => item.key === this.kavansicht())?.value;
    this.kavTitel = this.mrTranslate.transform(value) + ': ' + this.gesKAV;
    this.curgraphkav = this.graphkav1;

    if (this.kavansicht() == 3) {
      this.curgraphkav = this.graphkav2;
      this.kavTitel = this.mrTranslate.transform(value);
    }
    if (this.kavansicht() == 4) {
      this.curgraphkav = this.graphkav3;
      this.kavTitel = this.mrTranslate.transform(value);
    }
    if (this.kavansicht() == 10) {
      this.curgraphkav = this.graph;
      this.kavTitel = this.mrTranslate.transform(value);
    }
  }

  changeKostenStatus() {
    if (this.kostenansicht == 0) {
      this.kostenansicht = 1;
      this.kostenTitel = this.mrTranslate.transform('Kosten aktuellen Monat');
      return;
    }
    if (this.kostenansicht == 1) {
      this.kostenansicht = 2;
      this.kostenTitel = this.mrTranslate.transform('Kosten aktuelles Jahr');
      return;
    }
    if (this.kostenansicht == 2) {
      this.kostenansicht = 0;
      this.kostenTitel = this.mrTranslate.transform('Kosten aktuelle Woche');
      return;
    }
  }

  changeUserStatus() {
    if (this.useransicht == 0) {
      this.useransicht = 1;
      this.userTitel = this.mrTranslate.transform('Programmnutzung aktuellen Monat');
      return;
    }
    if (this.useransicht == 1) {
      this.useransicht = 2;
      this.userTitel = this.mrTranslate.transform('Programmnutzung aktuelles Jahr');
      return;
    }
    if (this.useransicht == 2) {
      this.useransicht = 0;
      this.userTitel = this.mrTranslate.transform('Programmnutzung aktuelle Woche');
      return;
    }
  }

  saveOpt() {
    let val = this.optGroup.value;
    localStorage.setItem('DashboardSettings', JSON.stringify(val));

    this.getSettings();

    let send = {
      typ: 'dashboardsettings',
      value: val,
    };

    this.apiService
      .getStatistik(send)
      .pipe(first())
      .subscribe((res: any) => {
        if (res.success) {
          this.toastr.success(
            this.mrTranslate.transform('Erfolgreich gespeichert'),
            this.mrTranslate.transform('Einstellungen')
          );
        } else {
          this.toastr.error(
            this.mrTranslate.transform('Ein Fehler ist aufgetreten'),
            this.mrTranslate.transform('Einstellungen')
          );
        }

        setTimeout(() => {
          this.opened = false;
        }, 500);
      });
  }

  getSettings() {
    let val = JSON.parse(localStorage.getItem('DashboardSettings'));
    if (val) {
      this.optGroup.controls.show_BG.setValue(val.show_BG);
      this.optGroup.controls.show_Sperr.setValue(val.show_Sperr);
      this.optGroup.controls.show_Stoer.setValue(val.show_Stoer);
      this.optGroup.controls.show_LF.setValue(val.show_LF);
      this.optGroup.controls.show_Sonder.setValue(val.show_Sonder);
      this.optGroup.controls.show_Anz_Insp.setValue(val.show_Anz_Insp);
      this.optGroup.controls.show_Anz_Wart.setValue(val.show_Anz_Wart);
      this.optGroup.controls.show_Anz_Auf.setValue(val.show_Anz_Auf);
      this.optGroup.controls.show_Anz_Begeh.setValue(val.show_Anz_Begeh);
      this.optGroup.controls.show_Anz_Sonder.setValue(val.show_Anz_Sonder);
      this.optGroup.controls.show_Dia_FK.setValue(val.show_Dia_FK);
      this.optGroup.controls.show_Dia_KAV.setValue(val.show_Dia_KAV);
      this.optGroup.controls.show_Dia_Kost.setValue(val.show_Dia_Kost);
      this.optGroup.controls.show_Dia_Prog.setValue(val.show_Dia_Prog);
      this.optGroup.controls.show_Anz_ALV.setValue(val.show_Anz_ALV);
      this.optGroup.controls.show_Anz_EBL.setValue(val.show_Anz_EBL);
      this.optGroup.controls.show_Dia_Inst.setValue(val.show_Dia_Inst);

      this.changeDet.detectChanges();
    }
  }
  getAnlagen() {
    this.basicStore
      .getComponentStoreAsync('anlagen')
      .pipe(first(val => val != undefined))
      .subscribe(componentStore => {
        componentStore = JSON.parse(componentStore);
        this.table = componentStore['anlagen'];
      });
  }
  onBarClick(event: any) {
    console.log('Clicked bar:', event);

    const point = event.points[0];

    console.log('Category:', point.x);
    console.log('Value:', point.y);
    console.log('Index:', point.pointIndex);
  }

  ngOnInit() {
    this.kavTitel = this.mrTranslate.transform('KAV Entwicklung');
    this.kostenTitel = this.mrTranslate.transform('Kosten aktuelle Woche');
    this.userTitel = this.mrTranslate.transform('Programmnutzung aktuelle Woche');

    if (this.table == undefined) {
      this.getAnlagen();
    }

    this.kavansicht.set(0);

    this.basicStore
      .getComponentStoreAsync('dashboard')
      .pipe(first(val => val != undefined))
      .subscribe(ret => {
        let dash = JSON.parse(ret);
        let res = dash.dashboard;
        if (!res) {
          return;
        }
        this.getSettings();
        this.fkstatistic = res.fk || [];
        this.fkstatistic[0].hole = 0.4;
        let fk = [];
        this.fkstatistic[0].labels.forEach((currentValue, index) => {
          let lb = this.fkstatistic[0].labels[index];
          let splitted = lb.split(' ');
          fk.push(parseInt(splitted[1]));

          this.fkstatistic[0].labels[index] = this.mrTranslate.transform(lb);
        });
        this.fkstatistic[0].fk = fk;
        this.fkstatistic[0].direction = 'clockwise';

        this.anzBG = res.anzBG || 0;
        this.anzSperr = res.anzSperr || 0;
        this.anzStoer = res.anzStoer || 0;
        this.anzLF = res.anzLF || 0;
        this.anzSonder = res.anzSonder || 0;
        this.anzInsp = res.anzInsp || 0;
        this.anzWart = res.anzWart || 0;
        this.anzAuf = res.anzAuf || 0;
        this.anzBegeh = res.anzBegeh || 0;
        this.hasBegeh = res.hasBegeh;
        this.gesKAV = res.gesKAV || [];

        let kav_erneu_weiche = res.kav3 || [];
        if (kav_erneu_weiche.length != 0) {
          kav_erneu_weiche[1].line = {
            shape: 'hvh',
            color: 'rgb(31, 119, 180)',
          };
          ((kav_erneu_weiche[1].name = 'Stk.'), (kav_erneu_weiche[0].mode = 'lines'));
          kav_erneu_weiche[0].line = { color: 'grey', dash: 'dot' };
        }

        let kav_erneu_gleis = res.kav4 || [];
        if (kav_erneu_gleis.length != 0) {
          kav_erneu_gleis[1].line = {
            shape: 'hvh',
            color: 'rgb(31, 119, 180)',
          };
          ((kav_erneu_gleis[1].name = 'm'), (kav_erneu_gleis[0].mode = 'lines'));
          kav_erneu_gleis[0].line = { color: 'grey', dash: 'dot' };
        }

        if (res.kav10 && res.kav10.length != 0) {
          res.kav10[0].direction = 'clockwise';
          res.kav10[0].hole = 0.4;
        }

        this.diagramm.update(d => ({
          ...d,
          0: res.kav0 || [],
          1: res.kav1 || [],
          2: res.kav2 || [],
          3: kav_erneu_weiche,
          4: kav_erneu_gleis,
          5: res.kav5 || [],
          6: res.kav6 || [],
          7: res.kav7 || [],
          8: res.kav8 || [],
          9: res.kav9 || [],
          10: res.kav10 || [],
        }));

        let hasKAV = res.kav0?.length > 0;

        this.InspStammIDs = (res.ostammidsInsp as number[]).map(ostammid => ({
          name: ostammid,
        }));

        this.kostenweek = res.kostenweek || [];
        this.kostenmonth = res.kostenmonth || [];
        this.kostenyear = res.kostenyear || [];

        this.userweek = res.userweek || [];
        this.usermonth = res.usermonth || [];
        this.useryear = res.useryear || [];

        this.curgraphkav = this.graphkav1;

        this.optChecked_EBL = res.optChecked_EBL || 0;
        this.anzALV = res.anzALV || 0;
        this.anzEBL = res.anzEBL || 0;

        this.anzSonderTermin = res.anzSonderTermin || 0;
        this.SondertOMAZSID = res.SondertOMAZSID || undefined;

        this.dtWRewNewdates = res.dtWRewNewdates || undefined;
        this.dtGRewNewdates = res.dtGRewNewdates || undefined;

        this.ih_qoute = res.ih_qoute || undefined;
        if (this.ih_qoute != undefined) {
          this.dia_ih_qoute = { ...this.graphfk };
          this.dia_ih_qoute.data = this.ih_qoute;
        }
        this.ih_average = res.ih_average || undefined;
        if (this.ih_average != undefined) {
          this.dia_ih_average = { ...this.graphfk };
          this.dia_ih_average.data = this.ih_average;
        }

        setTimeout(() => {
          if (!hasKAV) {
            this.kavansicht.set(0);
            this.optGroup.controls.show_Dia_KAV.setValue(false);
          }
        });
      });
  }

  protected qiLegentSub(el: PlotlyHTMLElement) {
    el?.on('plotly_legendclick', event => {
      if ('label' in event) this.qilegendclick(event.label as string);
      return true;
    });
  }

  protected fkLegentSub(el: PlotlyHTMLElement) {
    el?.on('plotly_legendclick', event => {
      if ('label' in event) this.fklegendclick(event.label as string);
      return true;
    });
  }

  private qilegendclick(qiLabel: string) {
    const qi = +qiLabel;
    if (Number.isNaN(qi)) return;

    const preFilters: FiltersMap = {
      ['QI']: {
        columnId: 'QI',
        items: [{ name: qi }],
        reason: ColumnFilterAction.Submit,
      },
    };

    sessionStorage.setItem('filteranlagen', JSON.stringify(preFilters));
    this.router.navigateByUrl('/anlagen');
  }

  private fklegendclick(fkLabel: string) {
    const fk = +fkLabel.split(' ').at(-1);
    if (Number.isNaN(fk)) return;

    const preFilters: FiltersMap = {
      ['fehlerklasse']: {
        columnId: 'fehlerklasse',
        items: [{ name: fk }],
        reason: ColumnFilterAction.Submit,
      },
    };

    sessionStorage.setItem('filtermangel', JSON.stringify(preFilters));
    this.router.navigateByUrl('/maengel');
  }

  private setPreFilters(typ: string): FiltersMap {
    switch (typ) {
      case 'termin':
        return {
          termin: {
            columnId: 'termin',
            items: [],
            reason: ColumnFilterAction.Redirect,
          },
          erlstatus: {
            columnId: 'erlstatus',
            items: [{ name: 0 }],
            reason: ColumnFilterAction.Submit,
          },
        };
      case 'betriebsgefahr':
        return {
          betriebsgefahr: {
            columnId: 'betriebsgefahr',
            items: [{ name: true }],
            reason: ColumnFilterAction.Submit,
          },
        };
      case 'st':
      case 'si': {
        const columnId = typ.toUpperCase();
        return {
          [columnId]: {
            columnId,
            items: [{ name: 1 }],
            reason: ColumnFilterAction.Submit,
          },
        };
      }
      case 'ALV':
      case 'EBL': {
        const filteredData: FilterItem[] = this.table.rows.flatMap((row: Row) =>
          row.LPID != 0 && row.LPID != null ? [{ name: row.LPID }] : []
        );
        return {
          [typ]: {
            columnId: typ,
            items: [{ name: emptyValueSign, isEmptyValue: true }, { name: false }],
            reason: ColumnFilterAction.Submit,
          },
          ['LPID']: {
            columnId: 'LPID',
            items: filteredData,
            reason: ColumnFilterAction.Submit,
          },
        };
      }
      case 'lf': {
        const filteredData: FilterItem[] = this.table.rows.flatMap((row: Row) =>
          row.LF != 0 ? [{ name: row.LF }] : []
        );
        return {
          LF: {
            columnId: 'LF',
            items: filteredData,
            reason: ColumnFilterAction.Submit,
          },
        };
      }
      case 'sp': {
        const filteredData: FilterItem[] = this.table.rows.flatMap((row: Row) =>
          row.SP != 0 ? [{ name: row.SP }] : []
        );
        return {
          SP: {
            columnId: 'SP',
            items: filteredData,
            reason: ColumnFilterAction.Submit,
          },
        };
      }
      case 'OSTAMMID':
        return {
          OSTAMMID: {
            columnId: 'OSTAMMID',
            items: this.InspStammIDs,
            reason: ColumnFilterAction.Submit,
          },
        };
      default: {
        console.warn(`No predefined filter for type: ${typ}`);
        return null;
      }
    }
  }

  routing(url: string, typ: string): void {
    const preFilters = this.setPreFilters(typ);

    if (preFilters)
      switch (url) {
        case '/anlagen':
          sessionStorage.setItem('filteranlagen', JSON.stringify(preFilters));
          break;
        case '/auftraege':
          sessionStorage.setItem('filterauftraege', JSON.stringify(preFilters));
          break;
        case '/maengel':
          sessionStorage.setItem('filtermangel', JSON.stringify(preFilters));
          break;
      }

    this.router.navigateByUrl(url);
  }

  openKAVDetaildialog() {
    this.overlayService.setOverlay({
      overlay: 'kavdetaildialog',
      data: {
        graph: this.graph,
        graphkav0: this.graphkav0,
        graphkav1: this.graphkav1,
        graphkav2: this.graphkav2,
        graphkav3: this.graphkav3,
        diagramm: this.diagramm(),
        index: this.kavansicht(),
        gesKAV: this.gesKAV,
        dtWRewNewdates: this.dtWRewNewdates,
        dtGRewNewdates: this.dtGRewNewdates,
      },

      zIndex: 8,
    });
  }
}
